/**
 * Core Web Vitals, reported per device class (ROADMAP SEO row, item 6).
 *
 * Google ranks on CrUX's 75th percentile for mobile and desktop SEPARATELY, and
 * the phone now gets its own shell — so a phone regression would be invisible in
 * a blended number. Each metric is sent once per page load as `web_vital` with
 * the device class it was measured on, split on the same 900px line the phone
 * layout uses (`usePhoneViewport`: MUI `down('md')`).
 *
 * v4's `onINP` replaces FID, which Google retired from Core Web Vitals in 2024.
 */
import type { Metric } from 'web-vitals';
import analyticsService from './services/analytics';

/** MUI's `down('md')` query — keep in step with `phone/usePhoneViewport`. */
export const PHONE_MEDIA_QUERY = '(max-width:899.95px)';

export type ViewportClass = 'phone' | 'desktop';

export function viewportClass(): ViewportClass {
  return typeof window.matchMedia === 'function' && window.matchMedia(PHONE_MEDIA_QUERY).matches
    ? 'phone'
    : 'desktop';
}

/** The analytics payload for one metric. CLS is unitless, the rest are ms. */
export function toWebVitalEvent(metric: Pick<Metric, 'name' | 'value' | 'rating'>) {
  return {
    name: metric.name,
    value: Number(metric.value.toFixed(metric.name === 'CLS' ? 4 : 0)),
    rating: metric.rating,
    viewportClass: viewportClass(),
  };
}

export default function reportWebVitals(): void {
  import('web-vitals')
    .then(({ onCLS, onINP, onLCP }) => {
      const send = (metric: Metric) => analyticsService.logEvent('web_vital', toWebVitalEvent(metric));
      onLCP(send);
      onINP(send);
      onCLS(send);
    })
    .catch((error) => {
      // Measurement must never break the page; say so where it can be found.
      console.warn('[web-vitals] could not load', error);
    });
}
